import { InteractiveStatCard } from '@/components/dashboard/stats/interactive-stat-card';
import { TaskChart } from '@/components/dashboard/task-chart';
import { Building2, Clock, CheckCircle2, MessageSquare, Home, TrendingUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { motion } from 'framer-motion';

export function PropertyManagerDashboard() {
  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-primary/10 p-2 flex items-center justify-center">
            <Home className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-primary-700 dark:text-primary-300">
              Property Manager Dashboard
            </h1>
            <p className="text-muted-foreground">
              Overview of your properties and maintenance requests
            </p>
          </div>
        </div>
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-sm px-4 py-2 rounded-full glass-effect text-muted-foreground flex items-center gap-2"
        >
          <TrendingUp className="h-4 w-4 text-primary" />
          <span>94% Occupancy Rate</span>
        </motion.div>
      </motion.div>
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
      >
        <InteractiveStatCard
          title="Active Requests"
          value="12"
          description="+3 from last week"
          icon={Clock}
          trend={{
            data: [
              { name: 'Mon', value: 8 },
              { name: 'Tue', value: 10 },
              { name: 'Wed', value: 12 },
              { name: 'Thu', value: 9 },
              { name: 'Fri', value: 12 },
            ],
            description: "Request volume over the past week"
          }}
          details={{
            description: "Current active requests by priority and type",
            items: [
              { label: "High Priority", value: "4" },
              { label: "Medium Priority", value: "5" },
              { label: "Low Priority", value: "3" },
              { label: "Maintenance", value: "6" },
              { label: "Repairs", value: "4" },
              { label: "Inspections", value: "2" }
            ]
          }}
        />
        <InteractiveStatCard
          title="Properties"
          value="5"
          description="All active"
          icon={Building2}
          details={{
            description: "Property portfolio overview",
            items: [
              { label: "Total Units", value: "120" },
              { label: "Square Footage", value: "85,000 sq ft" },
              { label: "Occupancy Rate", value: "94%" },
              { label: "Active Properties", value: "5" }
            ]
          }}
        />
        <InteractiveStatCard
          title="Completed Tasks"
          value="48"
          description="+12% this month"
          icon={CheckCircle2}
          trend={{
            data: [
              { name: 'Week 1', value: 10 },
              { name: 'Week 2', value: 12 },
              { name: 'Week 3', value: 15 },
              { name: 'Week 4', value: 11 },
            ],
            description: "Task completion rate by week"
          }}
          details={{
            description: "Task completion statistics",
            items: [
              { label: "This Week", value: "12" },
              { label: "This Month", value: "48" },
              { label: "Avg Time to Complete", value: "2.5 days" },
              { label: "On-Time Rate", value: "95%" }
            ]
          }}
        />
        <InteractiveStatCard
          title="Feedback Rating"
          value="4.8"
          description="Based on 32 reviews"
          icon={MessageSquare}
          trend={{
            data: [
              { name: 'Jan', value: 4.6 },
              { name: 'Feb', value: 4.7 },
              { name: 'Mar', value: 4.8 },
              { name: 'Apr', value: 4.8 },
            ],
            description: "Average rating trend over time"
          }}
          details={{
            description: "Detailed feedback breakdown",
            items: [
              { label: "5 Star Reviews", value: "24" },
              { label: "4 Star Reviews", value: "6" },
              { label: "3 Star Reviews", value: "2" },
              { label: "Response Rate", value: "100%" }
            ]
          }}
        />
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid gap-6 md:grid-cols-7"
      >
        <Card variant="glass" className="col-span-4 p-1 overflow-hidden">
          <TaskChart />
        </Card>
        
        <Card variant="glass" className="col-span-3 p-6 overflow-hidden">
          <h3 className="font-semibold text-lg mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + (i * 0.1) }}
                className="flex items-start gap-3 p-3 rounded-lg bg-background/30 backdrop-blur-sm"
              >
                <div className="rounded-full bg-primary/10 p-2 flex items-center justify-center">
                  <Clock className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-sm">Maintenance request #{1000 + i} created</p>
                  <p className="text-xs text-muted-foreground">Property: Riverside Apartments</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(Date.now() - i * 3600000).toLocaleTimeString()}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </Card>
      </motion.div>
    </div>
  );
}